/**
 * Highlights Library SSO Integration Service
 * Handles launching Highlights Library with SSO authentication
 */

/**
 * Launches Highlights Library SSO for a user
 * @param {string} userId - The user ID to authenticate
 * @param {Object} options - Optional configuration
 * @param {number} options.ttlSeconds - Token time-to-live (60-1800 seconds)
 * @param {string} options.returnUrl - URL to return to after session
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function launchHighlightsLibrarySso(userId, options = {}) {
  try {
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('REACT_APP_ADMIN_API_KEY not configured');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      throw new Error('REACT_APP_API_BASE_URL is not configured');
    }

    const normalizedBaseUrl = apiBaseUrl.replace(/\/$/, '').replace(/\/api$/, '');
    const tokenUrl = `${normalizedBaseUrl}/api/providers/highlights-library/issue-token`;

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': adminApiKey,
      },
      body: JSON.stringify({
        userId: userId,
        ttlSeconds: options.ttlSeconds || 300,
        returnUrl: options.returnUrl || window.location.origin,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to issue token');
    }

    // Open Highlights Library in new tab
    const redirectUrl = data.redirectUrl || data.launchUrl;
    if (!redirectUrl) {
      throw new Error('No redirect URL received from server');
    }

    window.open(redirectUrl, '_blank', 'noopener,noreferrer');

    return {
      success: true,
      token: data.token,
      launchUrl: data.launchUrl,
      expiresAt: data.expiresAt,
    };

  } catch (error) {
    return {
      success: false,
      error: getHighlightsLibraryErrorMessage(error.message),
      details: error,
    };
  }
}

/**
 * Check if a provider is Highlights Library
 * @param {Object} provider - Provider object from database
 * @returns {boolean} True if this is Highlights Library provider
 */
export function isHighlightsLibraryProvider(provider) {
  if (!provider) return false;

  // Check by provider key
  if (provider.key === 'highlights-library' ||
    provider.key === 'highlights_library' ||
    provider.key === 'highlights') {
    return true;
  }

  // Check by name (fallback)
  const name = (provider.name || '').toLowerCase();
  return name.includes('highlights') && (name.includes('library') || name.includes('sso'));
}

/**
 * Get user-friendly error messages for Highlights Library SSO errors
 * @param {string} error - Error message or code
 * @returns {string} User-friendly error message
 */
export function getHighlightsLibraryErrorMessage(error) {
  const errorStr = (error || '').toLowerCase();

  // Highlights Library API error codes
  if (errorStr.includes('0001') || errorStr.includes('wrong param')) {
    return 'Configuration error with company code. Please contact support.';
  }
  if (errorStr.includes('0002')) {
    return 'Invalid user type. Please contact support.';
  }
  if (errorStr.includes('0003')) {
    return 'Invalid user information. Please contact support.';
  }
  if (errorStr.includes('0004') || errorStr.includes('not valid company_code')) {
    return 'Company not registered with Highlights Library. Please contact support.';
  }
  if (errorStr.includes('0005')) {
    return 'SSO not enabled for this company. Please contact support.';
  }
  if (errorStr.includes('0006') || errorStr.includes('0007') || errorStr.includes('duplicate')) {
    return 'Duplicate account detected. Please contact support.';
  }
  if (errorStr.includes('0010') || errorStr.includes('id length')) {
    return 'Username must be between 4-35 characters (letters and numbers only).';
  }
  if (errorStr.includes('0011') || errorStr.includes('name length')) {
    return 'Name must be between 4-35 characters.';
  }

  // Generic errors
  if (errorStr.includes('unauthorized') || errorStr.includes('api key')) {
    return 'Authentication failed. Please contact support.';
  }
  if (errorStr.includes('network') || errorStr.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  if (errorStr.includes('user_not_found')) {
    return 'User account not found. Please contact support.';
  }

  return error || 'An unexpected error occurred. Please try again.';
}
